import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { C } from '../utils/theme.js';
import { t } from '../utils/i18n.js';
import Card from './Card.jsx';
import QSLDesignStudio from './QSLDesignStudio.jsx';
import { drawDesign, collectImageSrcs, SAMPLE_DATA } from '../utils/qslRender.js';

var TEMPLATES = [
  { id: "dxhunter",  name: "DX Hunter",  accent: "#00d4ff" },
  { id: "contest",   name: "Contest",    accent: "#e94560" },
  { id: "satellite",  name: "Satellite",  accent: "#a855f7" },
  { id: "digital",   name: "Digital",    accent: "#00ff88" },
  { id: "tropical",  name: "Tropical",   accent: "#38ef7d" },
  { id: "cw",        name: "CW/Morse",   accent: "#f39c12" },
  { id: "sunset",    name: "Sunset",     accent: "#ff6b35" },
  { id: "aurora",    name: "Aurora",     accent: "#00ffa3" }
];

// ── Helper drawing functions ──

function drawBrazilFlag(ctx, x, y, w, h) {
  // Green rectangle
  ctx.fillStyle = "#009c3b";
  ctx.fillRect(x, y, w, h);
  // Yellow diamond
  ctx.fillStyle = "#ffdf00";
  ctx.beginPath();
  ctx.moveTo(x + w * 0.5, y + h * 0.08);
  ctx.lineTo(x + w * 0.95, y + h * 0.5);
  ctx.lineTo(x + w * 0.5, y + h * 0.92);
  ctx.lineTo(x + w * 0.05, y + h * 0.5);
  ctx.closePath(); ctx.fill();
  // Blue circle
  ctx.fillStyle = "#002776";
  ctx.beginPath();
  ctx.arc(x + w * 0.5, y + h * 0.5, h * 0.28, 0, Math.PI * 2);
  ctx.fill();
  // White band
  ctx.strokeStyle = "#fff";
  ctx.lineWidth = h * 0.04;
  ctx.beginPath();
  ctx.arc(x + w * 0.5, y + h * 0.9, h * 0.65, Math.PI * 1.2, Math.PI * 1.8);
  ctx.stroke();
  // Stars
  ctx.fillStyle = "#fff";
  var stars = [[0.42,0.38],[0.52,0.36],[0.58,0.42],[0.48,0.58],[0.55,0.55],[0.44,0.50],[0.62,0.50],[0.38,0.46]];
  for (var i = 0; i < stars.length; i++) {
    ctx.beginPath();
    ctx.arc(x + w * stars[i][0], y + h * stars[i][1], 1.5, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawAntennaTower(ctx, x, baseY, height, color) {
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  // Main mast
  ctx.beginPath(); ctx.moveTo(x, baseY); ctx.lineTo(x, baseY - height); ctx.stroke();
  // Cross beams (yagi)
  var beams = [0.15, 0.30, 0.50, 0.70];
  for (var i = 0; i < beams.length; i++) {
    var by = baseY - height * beams[i];
    var bw = 25 - i * 4;
    ctx.beginPath(); ctx.moveTo(x - bw, by); ctx.lineTo(x + bw, by); ctx.stroke();
    // Director elements
    ctx.fillStyle = color;
    ctx.beginPath(); ctx.arc(x - bw, by, 1.5, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(x + bw, by, 1.5, 0, Math.PI * 2); ctx.fill();
  }
  // Top element
  ctx.beginPath(); ctx.moveTo(x, baseY - height); ctx.lineTo(x, baseY - height - 12); ctx.stroke();
  ctx.fillStyle = "#ff3333";
  ctx.beginPath(); ctx.arc(x, baseY - height - 12, 3, 0, Math.PI * 2); ctx.fill();
}

function drawRadioWaves(ctx, x, y, color, count) {
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  for (var i = 1; i <= count; i++) {
    ctx.globalAlpha = 1 - (i / (count + 1));
    ctx.beginPath();
    ctx.arc(x, y, 10 + i * 14, -Math.PI * 0.4, Math.PI * 0.4);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

function drawMountains(ctx, w, baseY, color1, color2) {
  // Back mountains
  ctx.fillStyle = color1;
  ctx.beginPath(); ctx.moveTo(0, baseY);
  ctx.lineTo(80, baseY - 80); ctx.lineTo(200, baseY - 140); ctx.lineTo(320, baseY - 60);
  ctx.lineTo(420, baseY - 120); ctx.lineTo(550, baseY - 170); ctx.lineTo(650, baseY - 90);
  ctx.lineTo(780, baseY - 130); ctx.lineTo(w, baseY - 50); ctx.lineTo(w, baseY);
  ctx.closePath(); ctx.fill();
  // Front mountains
  ctx.fillStyle = color2;
  ctx.beginPath(); ctx.moveTo(0, baseY);
  ctx.lineTo(120, baseY - 70); ctx.lineTo(250, baseY - 110); ctx.lineTo(380, baseY - 50);
  ctx.lineTo(500, baseY - 100); ctx.lineTo(630, baseY - 60); ctx.lineTo(750, baseY - 90);
  ctx.lineTo(w, baseY - 30); ctx.lineTo(w, baseY);
  ctx.closePath(); ctx.fill();
}

function drawStars(ctx, w, h, count, color) {
  ctx.fillStyle = color;
  var seed = 42;
  for (var i = 0; i < count; i++) {
    seed = (seed * 16807 + 0) % 2147483647;
    var sx = (seed % w);
    seed = (seed * 16807 + 0) % 2147483647;
    var sy = (seed % (h * 0.5));
    var size = 0.5 + (seed % 20) / 20;
    ctx.globalAlpha = 0.3 + (seed % 70) / 100;
    ctx.beginPath(); ctx.arc(sx, sy, size, 0, Math.PI * 2); ctx.fill();
  }
  ctx.globalAlpha = 1;
}

function drawSouthernCross(ctx, x, y, size, color) {
  ctx.fillStyle = color;
  var pts = [[0, -1.2], [0, 1.2], [-0.9, 0], [0.9, 0], [0.3, 0.6]];
  for (var i = 0; i < pts.length; i++) {
    var sx = x + pts[i][0] * size;
    var sy = y + pts[i][1] * size;
    // 4-point star shape
    ctx.beginPath();
    ctx.moveTo(sx, sy - 4); ctx.lineTo(sx + 1.5, sy); ctx.lineTo(sx, sy + 4);
    ctx.lineTo(sx - 1.5, sy); ctx.closePath(); ctx.fill();
  }
}

function drawPalmTree(ctx, x, baseY, h, trunkColor, leafColor) {
  // Trunk
  ctx.strokeStyle = trunkColor;
  ctx.lineWidth = 5;
  ctx.beginPath();
  ctx.moveTo(x, baseY);
  ctx.quadraticCurveTo(x + 8, baseY - h * 0.5, x + 3, baseY - h);
  ctx.stroke();
  // Leaves
  ctx.strokeStyle = leafColor;
  ctx.lineWidth = 2.5;
  var leaves = [[-40, 15], [40, 12], [-30, -10], [35, -8], [-20, 25], [25, 22], [0, -18]];
  for (var i = 0; i < leaves.length; i++) {
    ctx.beginPath();
    ctx.moveTo(x + 3, baseY - h);
    ctx.quadraticCurveTo(x + 3 + leaves[i][0] * 0.5, baseY - h + leaves[i][1] * 0.3, x + 3 + leaves[i][0], baseY - h + leaves[i][1]);
    ctx.stroke();
  }
}

// ── QSO data panel (shared across templates) ──
// Parse a color (hex or rgba) into {r,g,b,a}
function parseColor(c) {
  if (!c) return { r: 255, g: 255, b: 255, a: 1 };
  c = c.trim();
  var m = c.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})?$/i);
  if (m) return { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16), a: m[4] ? parseInt(m[4], 16) / 255 : 1 };
  var rm = c.match(/rgba?\(([^)]+)\)/i);
  if (rm) { var p = rm[1].split(",").map(function (s) { return parseFloat(s); }); return { r: p[0], g: p[1], b: p[2], a: p[3] == null ? 1 : p[3] }; }
  return { r: 255, g: 255, b: 255, a: 1 };
}
function rgba(o, a) { return "rgba(" + Math.round(o.r) + "," + Math.round(o.g) + "," + Math.round(o.b) + "," + (a == null ? o.a : a) + ")"; }

function drawQSOPanel(ctx, data, x, y, w, panelH, bgColor, labelColor, valueColor, borderColor) {
  var accent = parseColor(labelColor);
  var rad = 16;

  // Drop shadow for depth (glass card lift)
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.45)";
  ctx.shadowBlur = 22; ctx.shadowOffsetY = 8;
  ctx.fillStyle = "rgba(8,12,28,0.30)";
  ctx.beginPath(); ctx.roundRect(x, y, w, panelH, rad); ctx.fill();
  ctx.restore();

  // Glass gradient fill (lighter top → darker bottom)
  var gg = ctx.createLinearGradient(x, y, x, y + panelH);
  gg.addColorStop(0, rgba(accent, 0.16));
  gg.addColorStop(0.5, rgba(accent, 0.07));
  gg.addColorStop(1, "rgba(5,8,20,0.22)");
  ctx.fillStyle = gg;
  ctx.beginPath(); ctx.roundRect(x, y, w, panelH, rad); ctx.fill();

  // Top sheen highlight
  ctx.save();
  ctx.beginPath(); ctx.roundRect(x, y, w, panelH, rad); ctx.clip();
  var sheen = ctx.createLinearGradient(x, y, x, y + panelH * 0.45);
  sheen.addColorStop(0, "rgba(255,255,255,0.10)");
  sheen.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = sheen;
  ctx.fillRect(x, y, w, panelH * 0.45);
  ctx.restore();

  // Accent border + subtle inner outline
  ctx.strokeStyle = rgba(accent, 0.55); ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.roundRect(x, y, w, panelH, rad); ctx.stroke();
  ctx.strokeStyle = "rgba(255,255,255,0.07)"; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.roundRect(x + 2.5, y + 2.5, w - 5, panelH - 5, rad - 2); ctx.stroke();

  // Left accent bar
  ctx.fillStyle = rgba(accent, 0.9);
  ctx.beginPath(); ctx.roundRect(x + 7, y + 10, 4, panelH - 20, 2); ctx.fill();

  var fields = [
    ["DATE", data.date ? data.date.substring(0, 4) + "/" + data.date.substring(4, 6) + "/" + data.date.substring(6, 8) : "—"],
    ["TIME", data.time ? data.time.substring(0, 2) + ":" + data.time.substring(2, 4) + " UTC" : "—"],
    ["BAND", data.band || "—"],
    ["MODE", data.mode || "—"],
    ["FREQ", data.freq ? data.freq + " MHz" : "—"],
    ["RST TX", data.rst_sent || "59"],
    ["RST RX", data.rst_rcvd || "59"],
    ["GRID", data.grid || "—"]
  ];

  var cols = 4, rows = 2;
  var padX = 22, padY = 14;
  var cellW = (w - padX * 2) / cols, cellH = (panelH - padY * 2) / rows;
  var sx = x + padX, sy = y + padY;

  // Column dividers (subtle gradient lines)
  ctx.globalAlpha = 0.28;
  ctx.lineWidth = 1;
  for (var ci = 1; ci < cols; ci++) {
    var lx = sx + ci * cellW;
    var lg = ctx.createLinearGradient(lx, y + 14, lx, y + panelH - 14);
    lg.addColorStop(0, "rgba(255,255,255,0)");
    lg.addColorStop(0.5, rgba(accent, 0.8));
    lg.addColorStop(1, "rgba(255,255,255,0)");
    ctx.strokeStyle = lg;
    ctx.beginPath(); ctx.moveTo(lx, y + 14); ctx.lineTo(lx, y + panelH - 14); ctx.stroke();
  }
  // Horizontal mid divider
  var mg = ctx.createLinearGradient(x + 16, 0, x + w - 16, 0);
  mg.addColorStop(0, "rgba(255,255,255,0)");
  mg.addColorStop(0.5, rgba(accent, 0.7));
  mg.addColorStop(1, "rgba(255,255,255,0)");
  ctx.strokeStyle = mg;
  ctx.beginPath(); ctx.moveTo(x + 16, y + panelH / 2); ctx.lineTo(x + w - 16, y + panelH / 2); ctx.stroke();
  ctx.globalAlpha = 1;

  for (var fi = 0; fi < fields.length; fi++) {
    var col = fi % cols, row = Math.floor(fi / cols);
    var cx = sx + col * cellW + cellW / 2;
    var cy = sy + row * cellH;
    // Label (uppercase, tracked, dim)
    ctx.fillStyle = labelColor;
    ctx.globalAlpha = 0.7;
    ctx.font = "bold 10px 'Courier New',monospace";
    ctx.textAlign = "center";
    ctx.fillText(fields[fi][0], cx, cy + 13);
    ctx.globalAlpha = 1;
    // Value (bright, slight glow)
    ctx.save();
    ctx.shadowColor = rgba(accent, 0.5); ctx.shadowBlur = 6;
    ctx.fillStyle = valueColor;
    ctx.font = "bold 19px 'Courier New',monospace";
    ctx.fillText(fields[fi][1], cx, cy + 37);
    ctx.restore();
  }
}

// ── Main draw function per template ──
function drawQSLCard(canvas, tpl, data) {
  var ctx = canvas.getContext("2d");
  var w = 900, h = 540;
  canvas.width = w; canvas.height = h;
  var my = data.my_callsign || "N0CALL";
  var to = data.to_callsign || "DX0CALL";
  var msg = data.message || "";

  // ═══════════════════════════════════════
  if (tpl.id === "dxhunter") {
    // Dark blue space background
    var g2 = ctx.createRadialGradient(w * 0.5, h * 0.3, 50, w * 0.5, h * 0.5, w * 0.7);
    g2.addColorStop(0, "#0d2137"); g2.addColorStop(1, "#050d18");
    ctx.fillStyle = g2; ctx.fillRect(0, 0, w, h);
    drawStars(ctx, w, h, 100, "#ffffff");
    // Globe circle (Earth)
    ctx.strokeStyle = "#00d4ff20"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.arc(w * 0.5, h * 0.65, 250, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.ellipse(w * 0.5, h * 0.65, 250, 100, 0, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.ellipse(w * 0.5, h * 0.65, 100, 250, 0, 0, Math.PI * 2); ctx.stroke();
    // Grid lines
    for (var gi = 0; gi < 5; gi++) {
      ctx.beginPath(); ctx.moveTo(0, 80 + gi * 110); ctx.lineTo(w, 80 + gi * 110); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(100 + gi * 180, 0); ctx.lineTo(100 + gi * 180, h); ctx.stroke();
    }
    // Signal path line
    ctx.strokeStyle = "#00d4ff60"; ctx.lineWidth = 2; ctx.setLineDash([5, 5]);
    ctx.beginPath(); ctx.moveTo(100, h - 100); ctx.quadraticCurveTo(w * 0.5, 50, w - 100, h - 100); ctx.stroke();
    ctx.setLineDash([]);
    // Antenna bottom left
    drawAntennaTower(ctx, 70, h - 30, 160, "#00d4ff50");
    drawRadioWaves(ctx, 70, h - 190, "#00d4ff30", 4);
    // Antenna bottom right
    drawAntennaTower(ctx, w - 70, h - 30, 140, "#00d4ff30");
    // MY CALLSIGN
    ctx.shadowColor = "#00d4ff"; ctx.shadowBlur = 15;
    ctx.fillStyle = "#00d4ff"; ctx.font = "bold 62px 'Courier New',monospace"; ctx.textAlign = "center";
    ctx.fillText(my, w / 2, 85);
    ctx.shadowBlur = 0;
    // "DX HUNTER" badge
    ctx.fillStyle = "#00d4ff15"; ctx.beginPath(); ctx.roundRect(w / 2 - 80, 100, 160, 28, 14); ctx.fill();
    ctx.fillStyle = "#00d4ff"; ctx.font = "bold 12px 'Courier New',monospace";
    ctx.fillText("\u{1F30D} DX HUNTER", w / 2, 119);
    // Confirming + TO
    ctx.fillStyle = "#ffffffaa"; ctx.font = "14px 'Courier New',monospace"; ctx.fillText("Confirming QSO with", w / 2, 155);
    ctx.fillStyle = "#ffffff"; ctx.font = "bold 44px 'Courier New',monospace"; ctx.fillText(to, w / 2, 200);
    // QSO panel
    drawQSOPanel(ctx, data, 60, 225, w - 120, 105, "rgba(0,212,255,0.08)", "#00d4ff", "#e0f0ff", "#00d4ff25");
    // Grid + msg
    ctx.fillStyle = "#ffffff80"; ctx.font = "12px 'Courier New',monospace"; ctx.textAlign = "center";
    if (data.my_grid) ctx.fillText("My Grid: " + data.my_grid, w / 2, 355);
    if (msg) { ctx.font = "italic 13px 'Courier New',monospace"; ctx.fillText('"' + msg.substring(0, 70) + '"', w / 2, 378); }
    // Footer
    ctx.fillStyle = "rgba(0,0,0,0.5)"; ctx.fillRect(0, h - 38, w, 38);
    ctx.fillStyle = "#00d4ff60"; ctx.font = "10px 'Courier New',monospace"; ctx.textAlign = "center";
    ctx.fillText("HamExpert.cloud  \u2022  eQSL Card Manager  \u2022  73!", w / 2, h - 14);
  }

  // ═══════════════════════════════════════
  else if (tpl.id === "contest") {
    // Dark red/black aggressive
    var g3 = ctx.createLinearGradient(0, 0, w, h);
    g3.addColorStop(0, "#1a0a10"); g3.addColorStop(0.5, "#2d0a1a"); g3.addColorStop(1, "#0a0510");
    ctx.fillStyle = g3; ctx.fillRect(0, 0, w, h);
    // Red diagonal stripes
    ctx.strokeStyle = "#e9456012"; ctx.lineWidth = 40;
    for (var si = 0; si < 8; si++) { ctx.beginPath(); ctx.moveTo(-100 + si * 160, h + 50); ctx.lineTo(200 + si * 160, -50); ctx.stroke(); }
    // Top red bar
    ctx.fillStyle = "#e94560"; ctx.fillRect(0, 0, w, 6);
    // Bottom red bar
    ctx.fillStyle = "#e94560"; ctx.fillRect(0, h - 6, w, 6);
    // Trophy icon area
    ctx.fillStyle = "#e9456020"; ctx.beginPath(); ctx.arc(w - 80, 80, 50, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = "#e94560"; ctx.font = "40px serif"; ctx.textAlign = "center"; ctx.fillText("\u{1F3C6}", w - 80, 95);
    // MY CALLSIGN
    ctx.shadowColor = "#e94560"; ctx.shadowBlur = 20;
    ctx.fillStyle = "#fff"; ctx.font = "bold 68px 'Courier New',monospace"; ctx.textAlign = "center";
    ctx.fillText(my, w / 2 - 30, 90);
    ctx.shadowBlur = 0;
    // CONTEST STATION badge
    ctx.fillStyle = "#e94560"; ctx.beginPath(); ctx.roundRect(w / 2 - 100, 105, 200, 26, 4); ctx.fill();
    ctx.fillStyle = "#fff"; ctx.font = "bold 12px 'Courier New',monospace";
    ctx.fillText("CONTEST STATION", w / 2, 122);
    // TO
    ctx.fillStyle = "#ffffffaa"; ctx.font = "14px 'Courier New',monospace"; ctx.fillText("Confirming QSO with", w / 2, 158);
    ctx.fillStyle = "#e94560"; ctx.font = "bold 46px 'Courier New',monospace"; ctx.fillText(to, w / 2, 205);
    // QSO panel
    drawQSOPanel(ctx, data, 50, 225, w - 100, 105, "rgba(233,69,96,0.1)", "#e94560", "#ffe0e8", "#e9456025");
    // Info
    ctx.fillStyle = "#ffffff80"; ctx.font = "12px 'Courier New',monospace"; ctx.textAlign = "center";
    if (data.my_grid) ctx.fillText("My Grid: " + data.my_grid, w / 2, 355);
    if (msg) { ctx.font = "italic 13px 'Courier New',monospace"; ctx.fillText('"' + msg.substring(0, 70) + '"', w / 2, 378); }
    // Footer
    ctx.fillStyle = "rgba(233,69,96,0.15)"; ctx.fillRect(0, h - 38, w, 38);
    ctx.fillStyle = "#e9456080"; ctx.font = "10px 'Courier New',monospace"; ctx.textAlign = "center";
    ctx.fillText("HamExpert.cloud  \u2022  eQSL Card Manager  \u2022  73!", w / 2, h - 14);
  }

  // ═══════════════════════════════════════
  else if (tpl.id === "satellite") {
    // Deep space purple
    var g4 = ctx.createRadialGradient(w * 0.3, h * 0.3, 50, w * 0.5, h * 0.5, w * 0.8);
    g4.addColorStop(0, "#1a0d3a"); g4.addColorStop(0.5, "#0f0825"); g4.addColorStop(1, "#050210");
    ctx.fillStyle = g4; ctx.fillRect(0, 0, w, h);
    drawStars(ctx, w, h, 150, "#ffffff");
    // Orbit arcs
    ctx.strokeStyle = "#a855f720"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.ellipse(w * 0.5, h * 0.5, 350, 150, -0.3, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.ellipse(w * 0.5, h * 0.5, 280, 120, 0.2, 0, Math.PI * 2); ctx.stroke();
    // Satellite icon
    ctx.fillStyle = "#a855f7"; ctx.font = "36px serif"; ctx.textAlign = "right"; ctx.fillText("\u{1F6F0}\uFE0F", w - 40, 55);
    // Earth glow bottom
    var earthGlow = ctx.createRadialGradient(w * 0.5, h + 100, 50, w * 0.5, h + 100, 300);
    earthGlow.addColorStop(0, "#1a4a8a40"); earthGlow.addColorStop(1, "transparent");
    ctx.fillStyle = earthGlow; ctx.fillRect(0, h - 200, w, 200);
    // MY CALLSIGN
    ctx.shadowColor = "#a855f7"; ctx.shadowBlur = 15;
    ctx.fillStyle = "#e0d0ff"; ctx.font = "bold 60px 'Courier New',monospace"; ctx.textAlign = "center";
    ctx.fillText(my, w / 2, 90);
    ctx.shadowBlur = 0;
    // SAT badge
    ctx.fillStyle = "#a855f730"; ctx.beginPath(); ctx.roundRect(w / 2 - 70, 102, 140, 24, 12); ctx.fill();
    ctx.fillStyle = "#a855f7"; ctx.font = "bold 11px 'Courier New',monospace"; ctx.fillText("SATELLITE OPS", w / 2, 118);
    // TO
    ctx.fillStyle = "#ffffffaa"; ctx.font = "14px 'Courier New',monospace"; ctx.fillText("Confirming QSO with", w / 2, 155);
    ctx.fillStyle = "#a855f7"; ctx.font = "bold 44px 'Courier New',monospace"; ctx.fillText(to, w / 2, 198);
    drawQSOPanel(ctx, data, 55, 220, w - 110, 105, "rgba(168,85,247,0.08)", "#a855f7", "#e8d8ff", "#a855f720");
    ctx.fillStyle = "#ffffff80"; ctx.font = "12px 'Courier New',monospace"; ctx.textAlign = "center";
    if (data.my_grid) ctx.fillText("My Grid: " + data.my_grid, w / 2, 352);
    if (msg) { ctx.font = "italic 13px 'Courier New',monospace"; ctx.fillText('"' + msg.substring(0, 70) + '"', w / 2, 375); }
    ctx.fillStyle = "rgba(0,0,0,0.4)"; ctx.fillRect(0, h - 36, w, 36);
    ctx.fillStyle = "#a855f760"; ctx.font = "10px 'Courier New',monospace"; ctx.textAlign = "center";
    ctx.fillText("HamExpert.cloud  \u2022  eQSL Card Manager  \u2022  73!", w / 2, h - 13);
  }

  // ═══════════════════════════════════════
  else if (tpl.id === "digital") {
    // Matrix/hacker dark
    ctx.fillStyle = "#0a0a0a"; ctx.fillRect(0, 0, w, h);
    // Matrix rain columns
    ctx.font = "11px monospace"; ctx.fillStyle = "#00ff8808";
    var chars = "01アイウエオカキクケコ10サシスセソ";
    for (var mx = 0; mx < w; mx += 18) {
      for (var my2 = 0; my2 < h; my2 += 16) {
        if (Math.random() > 0.7) ctx.fillText(chars[Math.floor(Math.random() * chars.length)], mx, my2);
      }
    }
    // Scan lines
    ctx.fillStyle = "#00ff8805";
    for (var sl = 0; sl < h; sl += 3) { ctx.fillRect(0, sl, w, 1); }
    // Green border glow
    ctx.shadowColor = "#00ff88"; ctx.shadowBlur = 10;
    ctx.strokeStyle = "#00ff8840"; ctx.lineWidth = 2;
    ctx.strokeRect(8, 8, w - 16, h - 16); ctx.shadowBlur = 0;
    // Terminal header
    ctx.fillStyle = "#00ff8818"; ctx.fillRect(8, 8, w - 16, 35);
    ctx.fillStyle = "#00ff88"; ctx.font = "12px monospace"; ctx.textAlign = "left";
    ctx.fillText("root@hamexpert:~$ cat qsl_card.dat", 20, 30);
    // MY CALLSIGN
    ctx.shadowColor = "#00ff88"; ctx.shadowBlur = 12;
    ctx.fillStyle = "#00ff88"; ctx.font = "bold 58px 'Courier New',monospace"; ctx.textAlign = "center";
    ctx.fillText(my, w / 2, 100); ctx.shadowBlur = 0;
    // FT8/DIGITAL badge
    ctx.fillStyle = "#00ff8830"; ctx.beginPath(); ctx.roundRect(w / 2 - 60, 110, 120, 22, 4); ctx.fill();
    ctx.fillStyle = "#00ff88"; ctx.font = "bold 11px monospace"; ctx.fillText("DIGITAL MODE", w / 2, 125);
    // TO
    ctx.fillStyle = "#00ff8888"; ctx.font = "13px monospace"; ctx.fillText(">> QSO_WITH:", w / 2, 155);
    ctx.fillStyle = "#00ff88"; ctx.font = "bold 42px 'Courier New',monospace"; ctx.fillText(to, w / 2, 198);
    drawQSOPanel(ctx, data, 50, 218, w - 100, 105, "rgba(0,255,136,0.05)", "#00ff88", "#b0ffcc", "#00ff8820");
    ctx.fillStyle = "#00ff8880"; ctx.font = "12px monospace"; ctx.textAlign = "center";
    if (data.my_grid) ctx.fillText("[GRID] " + data.my_grid, w / 2, 348);
    if (msg) { ctx.font = "italic 12px monospace"; ctx.fillText("// " + msg.substring(0, 70), w / 2, 370); }
    ctx.fillStyle = "#00ff8815"; ctx.fillRect(8, h - 35, w - 16, 27);
    ctx.fillStyle = "#00ff8850"; ctx.font = "10px monospace"; ctx.textAlign = "center";
    ctx.fillText("[ HamExpert.cloud ]  [ eQSL Card Manager ]  [ 73 de " + my + " ]", w / 2, h - 17);
  }

  // ═══════════════════════════════════════
  else if (tpl.id === "tropical") {
    // Sky gradient
    var g6 = ctx.createLinearGradient(0, 0, 0, h);
    g6.addColorStop(0, "#1a8a6e"); g6.addColorStop(0.4, "#23aa78"); g6.addColorStop(0.7, "#2ecc71"); g6.addColorStop(1, "#1a6b4a");
    ctx.fillStyle = g6; ctx.fillRect(0, 0, w, h);
    // Sun
    var sunG = ctx.createRadialGradient(w * 0.8, 80, 10, w * 0.8, 80, 100);
    sunG.addColorStop(0, "#fff20080"); sunG.addColorStop(0.5, "#ffaa0030"); sunG.addColorStop(1, "transparent");
    ctx.fillStyle = sunG; ctx.fillRect(w * 0.6, 0, w * 0.4, 200);
    ctx.fillStyle = "#fff200"; ctx.beginPath(); ctx.arc(w * 0.8, 80, 35, 0, Math.PI * 2); ctx.fill();
    // Palm trees
    drawPalmTree(ctx, 70, h - 20, 180, "#5d4037", "#2e7d32");
    drawPalmTree(ctx, w - 90, h - 20, 160, "#5d4037", "#388e3c");
    drawPalmTree(ctx, 150, h - 20, 140, "#6d4c41", "#43a047");
    // Waves at bottom
    ctx.fillStyle = "#0288d180";
    ctx.beginPath(); ctx.moveTo(0, h - 60);
    for (var wi = 0; wi <= w; wi += 40) { ctx.quadraticCurveTo(wi + 10, h - 75, wi + 20, h - 60); ctx.quadraticCurveTo(wi + 30, h - 45, wi + 40, h - 60); }
    ctx.lineTo(w, h); ctx.lineTo(0, h); ctx.closePath(); ctx.fill();
    ctx.fillStyle = "#01579b50";
    ctx.beginPath(); ctx.moveTo(0, h - 40);
    for (var wi2 = 0; wi2 <= w; wi2 += 50) { ctx.quadraticCurveTo(wi2 + 15, h - 55, wi2 + 25, h - 40); ctx.quadraticCurveTo(wi2 + 35, h - 25, wi2 + 50, h - 40); }
    ctx.lineTo(w, h); ctx.lineTo(0, h); ctx.closePath(); ctx.fill();
    // MY CALLSIGN
    ctx.shadowColor = "#000"; ctx.shadowBlur = 10;
    ctx.fillStyle = "#fff"; ctx.font = "bold 62px 'Courier New',monospace"; ctx.textAlign = "center";
    ctx.fillText(my, w / 2, 95); ctx.shadowBlur = 0;
    ctx.fillStyle = "#ffffffcc"; ctx.font = "15px 'Courier New',monospace"; ctx.fillText("Confirming QSO with", w / 2, 130);
    ctx.fillStyle = "#fff200"; ctx.font = "bold 44px 'Courier New',monospace"; ctx.shadowBlur = 6; ctx.fillText(to, w / 2, 178); ctx.shadowBlur = 0;
    drawQSOPanel(ctx, data, 55, 200, w - 110, 105, "rgba(0,0,0,0.45)", "#fff200", "#fff", "#ffffff20");
    ctx.fillStyle = "#ffffffaa"; ctx.font = "12px 'Courier New',monospace"; ctx.textAlign = "center";
    if (data.my_grid) ctx.fillText("My Grid: " + data.my_grid, w / 2, 328);
    if (msg) { ctx.font = "italic 13px 'Courier New',monospace"; ctx.fillText('"' + msg.substring(0, 70) + '"', w / 2, 350); }
    ctx.fillStyle = "rgba(0,0,0,0.3)"; ctx.fillRect(0, h - 30, w, 30);
    ctx.fillStyle = "#ffffff70"; ctx.font = "10px 'Courier New',monospace"; ctx.textAlign = "center";
    ctx.fillText("HamExpert.cloud  \u2022  eQSL  \u2022  73!", w / 2, h - 10);
  }

  // ═══════════════════════════════════════
  else if (tpl.id === "cw") {
    // Warm dark teal
    var g7 = ctx.createLinearGradient(0, 0, w, h);
    g7.addColorStop(0, "#1a2a32"); g7.addColorStop(1, "#2c4050");
    ctx.fillStyle = g7; ctx.fillRect(0, 0, w, h);
    // Morse code background texture
    ctx.fillStyle = "#f39c1208"; ctx.font = "14px monospace";
    var morseLines = ["·· · ··· ·", "−·−· −−− −·· ·", "·−·· ··· −", "−−· ·−·· ···", "·−· ·− −·· ·· ···−"];
    for (var mli = 0; mli < morseLines.length; mli++) {
      ctx.textAlign = "left"; ctx.fillText(morseLines[mli], 20, 30 + mli * 110);
      ctx.textAlign = "right"; ctx.fillText(morseLines[mli], w - 20, 80 + mli * 110);
    }
    // Key/paddle icon
    ctx.fillStyle = "#f39c1220"; ctx.beginPath(); ctx.arc(80, h - 70, 40, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = "#f39c12"; ctx.font = "30px serif"; ctx.textAlign = "center"; ctx.fillText("\u{1F511}", 80, h - 60);
    // Gold borders
    ctx.strokeStyle = "#f39c1250"; ctx.lineWidth = 2; ctx.strokeRect(10, 10, w - 20, h - 20);
    ctx.strokeStyle = "#f39c1225"; ctx.lineWidth = 1; ctx.strokeRect(16, 16, w - 32, h - 32);
    // MY CALLSIGN
    ctx.shadowColor = "#f39c12"; ctx.shadowBlur = 10;
    ctx.fillStyle = "#f39c12"; ctx.font = "bold 60px 'Courier New',monospace"; ctx.textAlign = "center";
    ctx.fillText(my, w / 2, 90); ctx.shadowBlur = 0;
    // CW OPERATOR badge
    ctx.fillStyle = "#f39c1230"; ctx.beginPath(); ctx.roundRect(w / 2 - 65, 102, 130, 24, 6); ctx.fill();
    ctx.fillStyle = "#f39c12"; ctx.font = "bold 11px 'Courier New',monospace"; ctx.fillText("CW OPERATOR", w / 2, 118);
    ctx.fillStyle = "#ecf0f1aa"; ctx.font = "14px 'Courier New',monospace"; ctx.fillText("Confirming QSO with", w / 2, 155);
    ctx.fillStyle = "#f39c12"; ctx.font = "bold 44px 'Courier New',monospace"; ctx.fillText(to, w / 2, 198);
    drawQSOPanel(ctx, data, 55, 218, w - 110, 105, "rgba(243,156,18,0.08)", "#f39c12", "#ffecd0", "#f39c1220");
    ctx.fillStyle = "#ecf0f180"; ctx.font = "12px 'Courier New',monospace"; ctx.textAlign = "center";
    if (data.my_grid) ctx.fillText("My Grid: " + data.my_grid, w / 2, 348);
    if (msg) { ctx.font = "italic 13px 'Courier New',monospace"; ctx.fillText('"' + msg.substring(0, 70) + '"', w / 2, 370); }
    // Morse footer
    ctx.fillStyle = "rgba(0,0,0,0.3)"; ctx.fillRect(0, h - 38, w, 38);
    ctx.fillStyle = "#f39c1260"; ctx.font = "11px monospace"; ctx.textAlign = "center";
    ctx.fillText("··· · −· −   ···− ·· ·−   ···· ·− −− · −··− ·−−· · ·−· − ·−·−·− −·−· ·−·· ··−· ··− −··", w / 2, h - 14);
  }

  // ═══════════════════════════════════════
  else if (tpl.id === "sunset") {
    // Sunset gradient sky
    var g8 = ctx.createLinearGradient(0, 0, 0, h);
    g8.addColorStop(0, "#1a0533"); g8.addColorStop(0.25, "#4a1942"); g8.addColorStop(0.5, "#c7254e");
    g8.addColorStop(0.65, "#ff6b35"); g8.addColorStop(0.8, "#ffb347"); g8.addColorStop(1, "#2d1b3d");
    ctx.fillStyle = g8; ctx.fillRect(0, 0, w, h);
    // Sun
    ctx.fillStyle = "#ffb347"; ctx.beginPath(); ctx.arc(w * 0.5, h * 0.55, 80, 0, Math.PI, true); ctx.fill();
    ctx.fillStyle = "#ff6b3560"; ctx.beginPath(); ctx.arc(w * 0.5, h * 0.55, 100, 0, Math.PI, true); ctx.fill();
    // Mountains silhouette
    drawMountains(ctx, w, h * 0.55, "#1a082a", "#0d0418");
    // Antenna on mountain
    drawAntennaTower(ctx, 200, h * 0.55 - 110, 80, "#ff6b3560");
    drawRadioWaves(ctx, 200, h * 0.55 - 190, "#ff6b3530", 3);
    // MY CALLSIGN
    ctx.shadowColor = "#ff6b35"; ctx.shadowBlur = 12;
    ctx.fillStyle = "#fff"; ctx.font = "bold 62px 'Courier New',monospace"; ctx.textAlign = "center";
    ctx.fillText(my, w / 2, 80); ctx.shadowBlur = 0;
    ctx.fillStyle = "#ffffffcc"; ctx.font = "15px 'Courier New',monospace"; ctx.fillText("Confirming QSO with", w / 2, 115);
    ctx.fillStyle = "#ffb347"; ctx.font = "bold 44px 'Courier New',monospace"; ctx.shadowBlur = 8; ctx.fillText(to, w / 2, 160); ctx.shadowBlur = 0;
    drawQSOPanel(ctx, data, 55, 185, w - 110, 105, "rgba(0,0,0,0.55)", "#ffb347", "#fff", "#ff6b3530");
    ctx.fillStyle = "#ffffffaa"; ctx.font = "12px 'Courier New',monospace"; ctx.textAlign = "center";
    if (data.my_grid) ctx.fillText("My Grid: " + data.my_grid, w / 2, 315);
    if (msg) { ctx.font = "italic 13px 'Courier New',monospace"; ctx.fillText('"' + msg.substring(0, 70) + '"', w / 2, 338); }
    ctx.fillStyle = "rgba(0,0,0,0.5)"; ctx.fillRect(0, h - 34, w, 34);
    ctx.fillStyle = "#ffb34770"; ctx.font = "10px 'Courier New',monospace"; ctx.textAlign = "center";
    ctx.fillText("HamExpert.cloud  \u2022  eQSL Card Manager  \u2022  73!", w / 2, h - 12);
  }

  // ═══════════════════════════════════════
  else if (tpl.id === "aurora") {
    // Dark northern sky
    ctx.fillStyle = "#050818"; ctx.fillRect(0, 0, w, h);
    drawStars(ctx, w, h, 120, "#ffffff");
    // Aurora curtains
    var aColors = ["#00ffa340", "#00d4ff30", "#a855f730", "#00ff8830"];
    for (var ai = 0; ai < 6; ai++) {
      ctx.fillStyle = aColors[ai % aColors.length];
      ctx.beginPath(); ctx.moveTo(ai * 160 - 30, 0);
      ctx.quadraticCurveTo(ai * 160 + 40, h * 0.3, ai * 160 + 80, 0);
      ctx.quadraticCurveTo(ai * 160 + 120, h * 0.4, ai * 160 + 180, 0);
      ctx.lineTo(ai * 160 + 180, h * 0.5);
      ctx.quadraticCurveTo(ai * 160 + 90, h * 0.35, ai * 160 - 30, h * 0.5);
      ctx.closePath(); ctx.fill();
    }
    // Snowy mountains at bottom
    drawMountains(ctx, w, h - 40, "#0a1520", "#060d15");
    // Ground
    ctx.fillStyle = "#0a1520"; ctx.fillRect(0, h - 50, w, 50);
    // Antenna
    drawAntennaTower(ctx, w * 0.75, h - 50, 160, "#00ffa340");
    drawRadioWaves(ctx, w * 0.75, h - 210, "#00ffa320", 4);
    // MY CALLSIGN
    ctx.shadowColor = "#00ffa3"; ctx.shadowBlur = 15;
    ctx.fillStyle = "#00ffa3"; ctx.font = "bold 60px 'Courier New',monospace"; ctx.textAlign = "center";
    ctx.fillText(my, w / 2, 90); ctx.shadowBlur = 0;
    ctx.fillStyle = "#ffffffaa"; ctx.font = "14px 'Courier New',monospace"; ctx.fillText("Confirming QSO with", w / 2, 125);
    ctx.fillStyle = "#fff"; ctx.font = "bold 44px 'Courier New',monospace"; ctx.fillText(to, w / 2, 170);
    drawQSOPanel(ctx, data, 55, 192, w - 110, 105, "rgba(0,255,163,0.06)", "#00ffa3", "#d0fff0", "#00ffa320");
    ctx.fillStyle = "#ffffff80"; ctx.font = "12px 'Courier New',monospace"; ctx.textAlign = "center";
    if (data.my_grid) ctx.fillText("My Grid: " + data.my_grid, w / 2, 322);
    if (msg) { ctx.font = "italic 13px 'Courier New',monospace"; ctx.fillText('"' + msg.substring(0, 70) + '"', w / 2, 345); }
    ctx.fillStyle = "rgba(0,0,0,0.5)"; ctx.fillRect(0, h - 36, w, 36);
    ctx.fillStyle = "#00ffa360"; ctx.font = "10px 'Courier New',monospace"; ctx.textAlign = "center";
    ctx.fillText("HamExpert.cloud  \u2022  eQSL Card Manager  \u2022  73!", w / 2, h - 13);
  }

}

export default function QSLCardManager(props) {
  var lang = props.lang;
  var authToken = props.authToken;
  var qsos = props.qsos || [];

  // Tab state: select, preview, history
  var tabSt = useState("select");
  var tab = tabSt[0]; var setTab = tabSt[1];

  // QSO selection
  var selSt = useState({});
  var selected = selSt[0]; var setSelected = selSt[1];

  // Filters
  var filterSt = useState("");
  var filter = filterSt[0]; var setFilter = filterSt[1];
  var bandFilterSt = useState("");
  var bandFilter = bandFilterSt[0]; var setBandFilter = bandFilterSt[1];
  var modeFilterSt = useState("");
  var modeFilter = modeFilterSt[0]; var setModeFilter = modeFilterSt[1];

  // Pagination
  var pageSt = useState(0);
  var page = pageSt[0]; var setPage = pageSt[1];
  var PAGE_SIZE = 30;

  // Template
  var tplSt = useState("dxhunter");
  var tplId = tplSt[0]; var setTplId = tplSt[1];

  // Saved designs (Card Design Studio). selDesignId === "" means use legacy templates.
  var designsListSt = useState([]);
  var designsList = designsListSt[0]; var setDesignsList = designsListSt[1];
  var selDesignIdSt = useState("");
  var selDesignId = selDesignIdSt[0]; var setSelDesignId = selDesignIdSt[1];
  var designImagesRef = useRef({});
  var designImgVerSt = useState(0);
  var designImgVer = designImgVerSt[0]; var setDesignImgVer = designImgVerSt[1];

  // Inbox (received QSLs — Module C)
  var receivedSt = useState(null);
  var received = receivedSt[0]; var setReceived = receivedSt[1];

  function loadDesignsList() {
    fetch("/api/qsl/designs", { headers: { "Authorization": "Bearer " + authToken } })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d.designs) {
          setDesignsList(d.designs);
          // Auto-select default design the first time, if none chosen yet
          setSelDesignId(function(prev) {
            if (prev) return prev;
            var def = d.designs.find(function(x) { return x.is_default; });
            return def ? String(def.id) : prev;
          });
        }
      })
      .catch(function() {});
  }
  useEffect(function() { loadDesignsList(); }, [authToken]);

  function getSelectedDesign() {
    if (!selDesignId) return null;
    return designsList.find(function(d) { return String(d.id) === String(selDesignId); }) || null;
  }

  function loadReceived() {
    fetch("/api/qsl/received", { headers: { "Authorization": "Bearer " + authToken } })
      .then(function(r) { return r.json(); })
      .then(function(d) { setReceived(d); })
      .catch(function() {});
  }
  useEffect(function() { loadReceived(); }, [authToken]);

  function confirmReceived(id) {
    fetch("/api/qsl/received/" + id + "/confirm", { method: "POST", headers: { "Authorization": "Bearer " + authToken } })
      .then(function(r) { return r.json(); })
      .then(function() { loadReceived(); })
      .catch(function() {});
  }

  // Preload images referenced by the selected design
  useEffect(function() {
    var d = getSelectedDesign();
    if (!d) return;
    var srcs = collectImageSrcs(d.layout);
    srcs.forEach(function(src) {
      if (designImagesRef.current[src]) return;
      var im = new Image();
      im.onload = function() { designImagesRef.current[src] = im; setDesignImgVer(function(v) { return v + 1; }); };
      im.src = src;
    });
  }, [selDesignId, designsList]);

  function qsoToData(q) {
    return {
      my_callsign: q._myCall || props.myCallsign || "N0CALL",
      to_callsign: q.call || "",
      date: q.date || "", time: q.time || "", band: q.band || "", mode: q.mode || "",
      freq: q.freq || "", rst_sent: q.rstSent || "", rst_rcvd: q.rstRcvd || "",
      grid: q.grid || "", my_grid: q.myGrid || "", message: message
    };
  }

  // Message
  var msgSt = useState("");
  var message = msgSt[0]; var setMessage = msgSt[1];

  // Preview
  var previewQsoSt = useState(null);
  var previewQso = previewQsoSt[0]; var setPreviewQso = previewQsoSt[1];
  var canvasRef = useRef(null);

  // Send state
  var sendingSt = useState(false);
  var sending = sendingSt[0]; var setSending = sendingSt[1];
  var sendResultSt = useState(null);
  var sendResult = sendResultSt[0]; var setSendResult = sendResultSt[1];

  // Lookup state
  var lookupCacheSt = useState({});
  var lookupCache = lookupCacheSt[0]; var setLookupCache = lookupCacheSt[1];

  // History
  var historySt = useState(null);
  var history = historySt[0]; var setHistory = historySt[1];

  // Card preview modal
  var cardModalSt = useState(null);
  var cardModal = cardModalSt[0]; var setCardModal = cardModalSt[1];

  // Sent QSO IDs (to filter out already sent)
  var sentIdsSt = useState({});
  var sentIds = sentIdsSt[0]; var setSentIds = sentIdsSt[1];
  var sentCountSt = useState(0);
  var sentCount = sentCountSt[0]; var setSentCount = sentCountSt[1];

  // Load sent IDs on mount
  useEffect(function() {
    fetch("/api/qsl/history?limit=200", {
      headers: { "Authorization": "Bearer " + authToken }
    }).then(function(r) { return r.json(); })
      .then(function(d) {
        var ids = {};
        var count = 0;
        if (d.records) {
          for (var i = 0; i < d.records.length; i++) {
            if (d.records[i].status === "sent") {
              count++;
              if (d.records[i].qso_id) ids[d.records[i].qso_id] = true;
            }
          }
        }
        setSentIds(ids);
        setSentCount(count);
      })
      .catch(function() {});
  }, [authToken]);

  // Update sent IDs from queue status (includes queue-sent items)
  useEffect(function() {
    if (!queueStatus || !queueStatus.sent_qso_ids) return;
    var ids = Object.assign({}, sentIds);
    var changed = false;
    for (var i = 0; i < queueStatus.sent_qso_ids.length; i++) {
      var qid = queueStatus.sent_qso_ids[i];
      if (qid && !ids[qid]) { ids[qid] = true; changed = true; }
    }
    if (changed) {
      setSentIds(ids);
      setSentCount(Object.keys(ids).length);
    }
  }, [queueStatus]);

  // QSO IDs with "Email not found" error — removed from the main send list,
  // surfaced in the "Sem E-mail" tab so the operator can provide a manual email
  var noEmailIds = useMemo(function() {
    var ids = {};
    if (queueStatus && Array.isArray(queueStatus.no_email_qso_ids)) {
      for (var i = 0; i < queueStatus.no_email_qso_ids.length; i++) {
        var qid = queueStatus.no_email_qso_ids[i];
        if (qid) ids[qid] = true;
      }
    }
    return ids;
  }, [queueStatus]);
  var noEmailCount = Object.keys(noEmailIds).length;

  var pendingCount = qsos.length - sentCount - noEmailCount;

  // Unique bands/modes for filters
  var uniqueBands = useMemo(function() {
    var set = {};
    for (var i = 0; i < qsos.length; i++) if (qsos[i].band) set[qsos[i].band] = 1;
    return Object.keys(set).sort();
  }, [qsos]);

  var uniqueModes = useMemo(function() {
    var set = {};
    for (var i = 0; i < qsos.length; i++) if (qsos[i].mode) set[qsos[i].mode] = 1;
    return Object.keys(set).sort();
  }, [qsos]);

  // Filtered QSOs (exclude already sent AND no-email QSOs — these live on the "Sem E-mail" tab)
  var filtered = useMemo(function() {
    var result = qsos.filter(function(q) { return !sentIds[q.id] && !noEmailIds[q.id]; });
    if (filter) {
      var f = filter.toUpperCase();
      result = result.filter(function(q) { return (q.call || "").indexOf(f) >= 0; });
    }
    if (bandFilter) result = result.filter(function(q) { return q.band === bandFilter; });
    if (modeFilter) result = result.filter(function(q) { return q.mode === modeFilter; });
    return result;
  }, [qsos, filter, bandFilter, modeFilter, sentIds, noEmailIds]);

  var totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  var paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  var selectedCount = Object.keys(selected).filter(function(k) { return selected[k]; }).length;
  var selectedIds = Object.keys(selected).filter(function(k) { return selected[k]; }).map(Number);

  // Draw preview when template/design or qso changes
  useEffect(function() {
    if (!previewQso || !canvasRef.current) return;
    var design = getSelectedDesign();
    if (design) {
      var cv = canvasRef.current;
      cv.width = design.layout.w || 900;
      cv.height = design.layout.h || 540;
      drawDesign(cv.getContext("2d"), design.layout, qsoToData(previewQso), designImagesRef.current);
      return;
    }
    var tpl = TEMPLATES.find(function(tp) { return tp.id === tplId; }) || TEMPLATES[0];
    drawQSLCard(canvasRef.current, tpl, {
      my_callsign: previewQso._myCall || "N0CALL",
      to_callsign: previewQso.call || "",
      date: previewQso.date || "",
      time: previewQso.time || "",
      band: previewQso.band || "",
      mode: previewQso.mode || "",
      freq: previewQso.freq || "",
      rst_sent: previewQso.rstSent || "",
      rst_rcvd: previewQso.rstRcvd || "",
      grid: previewQso.grid || "",
      my_grid: previewQso.myGrid || "",
      message: message
    });
  }, [previewQso, tplId, message, selDesignId, designImgVer, designsList]);

  // Load history
  function loadHistory() {
    fetch("/api/qsl/history", {
      headers: { "Authorization": "Bearer " + authToken }
    }).then(function(r) { return r.json(); })
      .then(function(d) { setHistory(d); })
      .catch(function() {});
  }

  // Lookup email
  function lookupEmail(callsign, cb) {
    if (lookupCache[callsign]) return cb(lookupCache[callsign]);
    fetch("/api/qsl/lookup/" + encodeURIComponent(callsign), {
      headers: { "Authorization": "Bearer " + authToken }
    }).then(function(r) { return r.json(); })
      .then(function(d) {
        var newCache = Object.assign({}, lookupCache);
        newCache[callsign] = d;
        setLookupCache(newCache);
        cb(d);
      })
      .catch(function() { cb({ email: null, source: "error" }); });
  }

  // Send QSL
  function sendQSL(qso, email) {
    if (!canvasRef.current || sending) return;
    setSending(true);
    setSendResult(null);
    var cardImage = canvasRef.current.toDataURL("image/jpeg", 0.82);
    fetch("/api/qsl/send", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": "Bearer " + authToken
      },
      body: JSON.stringify({
        to_callsign: qso.call,
        to_email: email,
        template_id: tplId,
        qso_id: qso.id || null,
        card_image: cardImage,
        qso_data: {
          date: qso.date,
          band: qso.band,
          mode: qso.mode,
          rst_sent: qso.rstSent
        }
      })
    }).then(function(r) { return r.json(); })
      .then(function(d) {
        if (d.error) setSendResult({ ok: false, msg: d.error });
        else {
          setSendResult({ ok: true, msg: d.message || "QSL enviado!" });
          // Update sent tracking
          if (qso.id) {
            var newIds = Object.assign({}, sentIds);
            newIds[qso.id] = true;
            setSentIds(newIds);
          }
          setSentCount(sentCount + 1);
        }
        setSending(false);
      })
      .catch(function() {
        setSendResult({ ok: false, msg: "Erro de conexão" });
        setSending(false);
      });
  }

  // Download card as JPG
  function downloadCard() {
    if (!canvasRef.current || !previewQso) return;
    var link = document.createElement("a");
    link.download = "qsl_" + (previewQso.call || "card") + ".jpg";
    link.href = canvasRef.current.toDataURL("image/jpeg", 0.82);
    link.click();
  }

  // Batch send (legacy single)
  var batchSt = useState({ running: false, current: 0, total: 0, results: [] });
  var batch = batchSt[0]; var setBatch = batchSt[1];

  // Queue state — throttled email dispatch
  var queueStatusSt = useState(null);
  var queueStatus = queueStatusSt[0]; var setQueueStatus = queueStatusSt[1];
  var queueBusySt = useState(false);
  var queueBusy = queueBusySt[0]; var setQueueBusy = queueBusySt[1];
  var queueCountdownSt = useState(0);
  var queueCountdown = queueCountdownSt[0]; var setQueueCountdown = queueCountdownSt[1];
  var queuePreparingSt = useState(false);
  var queuePreparing = queuePreparingSt[0]; var setQueuePreparing = queuePreparingSt[1];
  var queuePrepProgressSt = useState({ current: 0, total: 0 });
  var queuePrepProgress = queuePrepProgressSt[0]; var setQueuePrepProgress = queuePrepProgressSt[1];

  // Poll queue status when active
  useEffect(function() {
    var interval = null;
    function fetchStatus() {
      fetch("/api/qsl/queue/status", {
        headers: { "Authorization": "Bearer " + authToken }
      }).then(function(r) { return r.json(); })
        .then(function(d) { setQueueStatus(d); })
        .catch(function() {});
    }
    fetchStatus();
    interval = setInterval(fetchStatus, 10000);
    return function() { clearInterval(interval); };
  }, [authToken]);

  // Countdown timer
  useEffect(function() {
    if (!queueStatus || (queueStatus.pending + queueStatus.sending) === 0) {
      setQueueCountdown(0);
      return;
    }
    setQueueCountdown(queueStatus.interval_seconds);
    var timer = setInterval(function() {
      setQueueCountdown(function(prev) {
        if (prev <= 1) return queueStatus.interval_seconds;
        return prev - 1;
      });
    }, 1000);
    return function() { clearInterval(timer); };
  }, [queueStatus]);

  // Queue batch send — generates card images and sends to backend queue
  // Backend handles email lookup via HamQTH/QRZ when processing
  function queueBatchSend() {
    if (queueBusy || queuePreparing) return;
    var selQsos = selectedIds.map(function(id) {
      return qsos.find(function(q) { return q.id === id; });
    }).filter(Boolean);
    if (selQsos.length === 0) return;

    // Pre-check daily limit before expensive card generation
    var remaining = queueStatus ? queueStatus.daily_limit - queueStatus.sent_today - (queueStatus.pending || 0) : 999;
    if (remaining <= 0) {
      setSendResult({ ok: false, msg: t(lang, "qslDailyLimitReached") || "Limite diário atingido. Tente novamente amanhã." });
      return;
    }

    setSendResult(null);
    setQueuePreparing(true);
    setQueuePrepProgress({ current: 0, total: selQsos.length });

    // Generate cards in chunks to allow React to update progress
    var tpl = TEMPLATES.find(function(tp) { return tp.id === tplId; }) || TEMPLATES[0];
    var design = getSelectedDesign();
    var offscreen = document.createElement("canvas");
    if (design) { offscreen.width = design.layout.w || 900; offscreen.height = design.layout.h || 540; }
    var items = [];
    var idx = 0;

    function generateNext() {
      var chunkEnd = Math.min(idx + 3, selQsos.length);
      try {
        for (; idx < chunkEnd; idx++) {
          var qso = selQsos[idx];
          if (design) {
            drawDesign(offscreen.getContext("2d"), design.layout, qsoToData(qso), designImagesRef.current);
          } else {
            drawQSLCard(offscreen, tpl, {
              my_callsign: qso._myCall || props.myCallsign || "N0CALL",
              to_callsign: qso.call || "",
              date: qso.date || "",
              time: qso.time || "",
              band: qso.band || "",
              mode: qso.mode || "",
              freq: qso.freq || "",
              rst_sent: qso.rstSent || "",
              rst_rcvd: qso.rstRcvd || "",
              grid: qso.grid || "",
              my_grid: qso.myGrid || "",
              message: message
            });
          }
          items.push({
            qso_id: qso.id,
            to_callsign: qso.call || "",
            to_email: "",
            template_id: design ? ("design:" + design.id) : tplId,
            card_image: offscreen.toDataURL("image/jpeg", 0.82),
            qso_data: { date: qso.date, band: qso.band, mode: qso.mode, rst_sent: qso.rstSent }
          });
        }
        setQueuePrepProgress({ current: idx, total: selQsos.length });
      } catch (ex) {
        setQueuePreparing(false);
        setQueueBusy(false);
        setSendResult({ ok: false, msg: "Erro ao gerar cards: " + ex.message });
        return;
      }

      if (idx < selQsos.length) {
        setTimeout(generateNext, 10);
        return;
      }

      // All cards generated — send to server
      setQueueBusy(true);

      fetch("/api/qsl/queue", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + authToken
        },
        body: JSON.stringify({ items: items })
      }).then(function(r) {
        return r.json().then(function(d) { return { status: r.status, ok: r.ok, data: d }; });
      }).then(function(result) {
          setQueuePreparing(false);
          setQueueBusy(false);
          var d = result.data;
          if (!result.ok || d.error) {
            setSendResult({ ok: false, msg: d.error || d.message || "Erro HTTP " + result.status });
          } else if (d.success === false) {
            setSendResult({ ok: false, msg: d.message });
          } else {
            var msg = d.message;
            if (d.skipped && d.skipped.length > 0) {
              msg += " | " + d.skipped.length + " ignorados";
            }
            setSendResult({ ok: true, msg: msg });
            setSelected({});
            fetch("/api/qsl/queue/status", {
              headers: { "Authorization": "Bearer " + authToken }
            }).then(function(r) { return r.json(); })
              .then(function(s) { setQueueStatus(s); });
          }
        })
        .catch(function(err) {
          setQueuePreparing(false);
          setQueueBusy(false);
          setSendResult({ ok: false, msg: "Erro de conexao: " + (err.message || "tente novamente") });
        });
    }

    setTimeout(generateNext, 50);
  }

  // Cancel queue
  function cancelQueue() {
    fetch("/api/qsl/queue/cancel", {
      method: "POST",
      headers: { "Authorization": "Bearer " + authToken }
    }).then(function(r) { return r.json(); })
      .then(function(d) {
        setSendResult({ ok: true, msg: d.message || "Fila cancelada" });
        fetch("/api/qsl/queue/status", {
          headers: { "Authorization": "Bearer " + authToken }
        }).then(function(r) { return r.json(); })
          .then(function(s) { setQueueStatus(s); });
      })
      .catch(function() { setSendResult({ ok: false, msg: "Erro ao cancelar" }); });
  }

  // Email input for manual entry
  var emailInputSt = useState("");
  var emailInput = emailInputSt[0]; var setEmailInput = emailInputSt[1];
  var lookupResultSt = useState(null);
  var lookupResult = lookupResultSt[0]; var setLookupResult = lookupResultSt[1];

  // Per-row email inputs for the "Sem E-mail" tab (keyed by queue item id)
  var noEmailInputsSt = useState({});
  var noEmailInputs = noEmailInputsSt[0]; var setNoEmailInputs = noEmailInputsSt[1];
  var noEmailResultSt = useState(null);
  var noEmailResult = noEmailResultSt[0]; var setNoEmailResult = noEmailResultSt[1];
  var noEmailBusySt = useState({});
  var noEmailBusy = noEmailBusySt[0]; var setNoEmailBusy = noEmailBusySt[1];

  function refreshQueueStatus() {
    fetch("/api/qsl/queue/status", {
      headers: { "Authorization": "Bearer " + authToken }
    }).then(function(r) { return r.json(); })
      .then(function(s) { setQueueStatus(s); })
      .catch(function() {});
  }

  function retryWithEmail(queueId, email) {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setNoEmailResult({ ok: false, msg: t(lang, "qslEmailInvalid") });
      return;
    }
    setNoEmailBusy(function(prev) { var n = Object.assign({}, prev); n[queueId] = true; return n; });
    fetch("/api/qsl/queue/retry-email", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": "Bearer " + authToken }
    , body: JSON.stringify({ queue_id: queueId, email: email })
    }).then(function(r) { return r.json().then(function(d) { return { ok: r.ok, data: d }; }); })
      .then(function(result) {
        setNoEmailBusy(function(prev) { var n = Object.assign({}, prev); delete n[queueId]; return n; });
        if (!result.ok || result.data.error) {
          setNoEmailResult({ ok: false, msg: result.data.error || "Erro" });
        } else {
          setNoEmailResult({ ok: true, msg: t(lang, "qslRetrySuccess") });
          setNoEmailInputs(function(prev) { var n = Object.assign({}, prev); delete n[queueId]; return n; });
          refreshQueueStatus();
        }
      })
      .catch(function() {
        setNoEmailBusy(function(prev) { var n = Object.assign({}, prev); delete n[queueId]; return n; });
        setNoEmailResult({ ok: false, msg: "Erro de conexao" });
      });
  }

  function dismissNoEmail(queueId) {
    if (!window.confirm(t(lang, "qslDismissConfirm"))) return;
    setNoEmailBusy(function(prev) { var n = Object.assign({}, prev); n[queueId] = true; return n; });
    fetch("/api/qsl/queue/dismiss-no-email", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": "Bearer " + authToken }
    , body: JSON.stringify({ queue_id: queueId })
    }).then(function(r) { return r.json().then(function(d) { return { ok: r.ok, data: d }; }); })
      .then(function(result) {
        setNoEmailBusy(function(prev) { var n = Object.assign({}, prev); delete n[queueId]; return n; });
        if (!result.ok || result.data.error) {
          setNoEmailResult({ ok: false, msg: result.data.error || "Erro" });
        } else {
          refreshQueueStatus();
        }
      })
      .catch(function() {
        setNoEmailBusy(function(prev) { var n = Object.assign({}, prev); delete n[queueId]; return n; });
        setNoEmailResult({ ok: false, msg: "Erro de conexao" });
      });
  }

  // Select all on current page
  function selectAllPage() {
    var newSel = Object.assign({}, selected);
    for (var i = 0; i < paged.length; i++) {
      var key = paged[i].id || i;
      newSel[key] = true;
    }
    setSelected(newSel);
  }

  function selectAllFiltered() {
    var newSel = Object.assign({}, selected);
    for (var i = 0; i < filtered.length; i++) {
      var key = filtered[i].id || i;
      newSel[key] = true;
    }
    setSelected(newSel);
  }

  function deselectAll() { setSelected({}); }

  // Check if all on current page are selected
  var allPageSelected = paged.length > 0 && paged.every(function(q) { return !!selected[q.id]; });
  var allFilteredSelected = filtered.length > 0 && filtered.every(function(q) { return !!selected[q.id]; });

  // Styles
  var btnStyle = { background: C.a1 + "18", color: C.a1, border: "1px solid " + C.a1 + "40", borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "monospace" };
  var btnAccent = { background: C.a2 + "18", color: C.a2, border: "1px solid " + C.a2 + "40", borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "monospace" };
  var btnWarn = { background: C.a3 + "18", color: C.a3, border: "1px solid " + C.a3 + "40", borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "monospace" };
  var inputStyle = { background: C.s1, border: "1px solid " + C.bd, borderRadius: 8, padding: "8px 12px", color: C.a1, fontSize: 13, fontFamily: "monospace", outline: "none" };
  var selectStyle = Object.assign({}, inputStyle, { color: C.t1 });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.a1, fontFamily: "'Courier New',monospace" }}>
          {"\u{1F4EE}"} {t(lang, "qslTitle")}
        </h2>
        <div style={{ display: "flex", gap: 6 }}>
          {[["design", t(lang, "qslDesignStudioTab")], ["select", t(lang, "qslSelectQsos")], ["preview", t(lang, "qslPreviewSend")], ["queue", t(lang, "qslQueue")], ["inbox", t(lang, "qslInboxTab")], ["noemail", t(lang, "qslNoEmailTab")], ["history", t(lang, "qslHistory")]].map(function(tb) {
            var active = tab === tb[0];
            var badge = tb[0] === "noemail" && noEmailCount > 0 ? " (" + noEmailCount + ")" : "";
            if (tb[0] === "inbox" && received && received.total > 0) badge = " (" + received.total + ")";
            return <button key={tb[0]} onClick={function() { setTab(tb[0]); if (tb[0] === "history") loadHistory(); if (tb[0] === "inbox") loadReceived(); if (tb[0] === "design" || tb[0] === "preview") loadDesignsList(); }} style={{
              background: active ? C.a1 : C.s2,
              color: active ? "#000" : C.t2,
              border: "1px solid " + (active ? C.a1 : C.bd),
              borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "monospace"
            }}>{tb[1]}{badge}</button>;
          })}
        </div>
      </div>

      {/* TAB: DESIGN STUDIO */}
      {tab === "design" && (
        <QSLDesignStudio lang={lang} authToken={authToken} onDesignSaved={loadDesignsList} />
      )}

      {/* TAB: INBOX (received QSLs) */}
      {tab === "inbox" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1, background: C.a3 + "10", border: "1px solid " + C.a3 + "30", borderRadius: 10, padding: "14px 18px", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ fontSize: 28 }}>{"\u{1F4E5}"}</div>
              <div>
                <div style={{ color: C.a3, fontSize: 22, fontWeight: 700, fontFamily: "monospace" }}>{received ? received.total : 0}</div>
                <div style={{ color: C.t2, fontSize: 11, fontFamily: "monospace" }}>{t(lang, "qslInboxReceived")}</div>
              </div>
            </div>
            <div style={{ flex: 1, background: C.a2 + "10", border: "1px solid " + C.a2 + "30", borderRadius: 10, padding: "14px 18px", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ fontSize: 28 }}>{"\u2705"}</div>
              <div>
                <div style={{ color: C.a2, fontSize: 22, fontWeight: 700, fontFamily: "monospace" }}>{received ? received.confirmed : 0}</div>
                <div style={{ color: C.t2, fontSize: 11, fontFamily: "monospace" }}>{t(lang, "qslInboxConfirmed")}</div>
              </div>
            </div>
          </div>
          {(!received || received.records.length === 0) ? (
            <Card style={{ padding: 40, textAlign: "center" }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>{"\u{1F4ED}"}</div>
              <div style={{ color: C.t3, fontSize: 14 }}>{t(lang, "qslInboxEmpty")}</div>
            </Card>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
              {received.records.map(function(r) {
                return (
                  <Card key={r.id} style={{ padding: 12 }}>
                    {r.card_path ? <img src={r.card_path} alt="QSL" style={{ width: "100%", borderRadius: 8, display: "block", marginBottom: 8, cursor: "pointer" }} onClick={function() { setCardModal({ path: r.card_path, callsign: r.from_callsign }); }} /> : null}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ color: C.a1, fontWeight: 700, fontFamily: "monospace", fontSize: 15 }}>{r.from_callsign}</span>
                      <span style={{ color: C.t3, fontSize: 10, fontFamily: "monospace" }}>{(r.received_at || "").substring(0, 10)}</span>
                    </div>
                    <div style={{ color: C.t3, fontSize: 11, fontFamily: "monospace", margin: "4px 0 8px" }}>
                      {(r.qso_data.band || "") + " " + (r.qso_data.mode || "")}
                    </div>
                    <button onClick={function() { confirmReceived(r.id); }} style={Object.assign({}, r.status === "confirmed" ? btnAccent : btnStyle, { width: "100%", boxSizing: "border-box" })}>
                      {r.status === "confirmed" ? "\u2705 " + t(lang, "qslInboxConfirmed") : t(lang, "qslInboxConfirm")}
                    </button>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* TAB: SELECT QSOs */}
      {tab === "select" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Indicators */}
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1, background: C.a2 + "10", border: "1px solid " + C.a2 + "30", borderRadius: 10, padding: "14px 18px", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ fontSize: 28 }}>{"\u2705"}</div>
              <div>
                <div style={{ color: C.a2, fontSize: 22, fontWeight: 700, fontFamily: "monospace" }}>{sentCount}</div>
                <div style={{ color: C.t2, fontSize: 11, fontFamily: "monospace" }}>{t(lang, "qslSent")}</div>
              </div>
            </div>
            <div style={{ flex: 1, background: C.a3 + "10", border: "1px solid " + C.a3 + "30", borderRadius: 10, padding: "14px 18px", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ fontSize: 28 }}>{"\u{1F4E8}"}</div>
              <div>
                <div style={{ color: C.a3, fontSize: 22, fontWeight: 700, fontFamily: "monospace" }}>{pendingCount > 0 ? pendingCount : 0}</div>
                <div style={{ color: C.t2, fontSize: 11, fontFamily: "monospace" }}>{t(lang, "qslPendingToSend")}</div>
              </div>
            </div>
          </div>
          {/* Filters */}
          <Card style={{ padding: 12 }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <input placeholder={t(lang, "qslSearchCall")} value={filter} onChange={function(e) { setFilter(e.target.value); setPage(0); }} style={Object.assign({}, inputStyle, { flex: 1, minWidth: 150 })} />
              <select value={bandFilter} onChange={function(e) { setBandFilter(e.target.value); setPage(0); }} style={selectStyle}>
                <option value="">{t(lang, "qslAllBands")}</option>
                {uniqueBands.map(function(b) { return <option key={b} value={b}>{b}</option>; })}
              </select>
              <select value={modeFilter} onChange={function(e) { setModeFilter(e.target.value); setPage(0); }} style={selectStyle}>
                <option value="">{t(lang, "qslAllModes")}</option>
                {uniqueModes.map(function(m) { return <option key={m} value={m}>{m}</option>; })}
              </select>
              <span style={{ color: C.t2, fontSize: 12, fontFamily: "monospace" }}>{filtered.length} QSOs</span>
              <button onClick={selectAllPage} style={Object.assign({}, btnStyle, allPageSelected ? { opacity: 0.5 } : {})}>{allPageSelected ? "\u2713 " : ""}{t(lang, "qslSelectPage")} ({paged.length})</button>
              <button onClick={selectAllFiltered} style={Object.assign({}, btnAccent, allFilteredSelected ? { opacity: 0.5 } : {})}>{allFilteredSelected ? "\u2713 " : ""}{t(lang, "qslSelectAll")} ({filtered.length})</button>
              <button onClick={deselectAll} style={Object.assign({}, btnWarn, selectedCount === 0 ? { opacity: 0.4 } : {})}>{t(lang, "qslClear")}</button>
            </div>
          </Card>

          {/* Selected counter */}
          {selectedCount > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: C.a2 + "12", border: "1px solid " + C.a2 + "30", borderRadius: 8, padding: "10px 16px" }}>
              <span style={{ color: C.a2, fontWeight: 600, fontSize: 13, fontFamily: "monospace" }}>{selectedCount} QSO{selectedCount > 1 ? "s" : ""} {t(lang, "qslSelected")}</span>
              <button onClick={function() { setTab("preview"); }} style={btnAccent}>{t(lang, "qslPreviewSend")} {"\u2192"}</button>
            </div>
          )}

          {/* QSO Table */}
          <Card style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: C.s3 }}>
                    <th style={{ padding: "10px 8px", width: 36, cursor: "pointer" }} onClick={function() { if (allPageSelected) { var n = Object.assign({}, selected); for (var i = 0; i < paged.length; i++) delete n[paged[i].id]; setSelected(n); } else { selectAllPage(); } }}>
                      <div style={{ width: 18, height: 18, borderRadius: 4, border: "2px solid " + (allPageSelected ? C.a1 : C.t3), background: allPageSelected ? C.a1 : "transparent", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto" }}>
                        {allPageSelected && <span style={{ color: "#000", fontSize: 12, fontWeight: 700 }}>{"\u2713"}</span>}
                      </div>
                    </th>
                    <th style={{ padding: "10px 8px", textAlign: "left", color: C.t2, fontFamily: "monospace", fontSize: 11 }}>CALLSIGN</th>
                    <th style={{ padding: "10px 8px", textAlign: "left", color: C.t2, fontFamily: "monospace", fontSize: 11 }}>DATE</th>
                    <th style={{ padding: "10px 8px", textAlign: "left", color: C.t2, fontFamily: "monospace", fontSize: 11 }}>TIME</th>
                    <th style={{ padding: "10px 8px", textAlign: "left", color: C.t2, fontFamily: "monospace", fontSize: 11 }}>BAND</th>
                    <th style={{ padding: "10px 8px", textAlign: "left", color: C.t2, fontFamily: "monospace", fontSize: 11 }}>MODE</th>
                    <th style={{ padding: "10px 8px", textAlign: "left", color: C.t2, fontFamily: "monospace", fontSize: 11 }}>RST S</th>
                    <th style={{ padding: "10px 8px", textAlign: "left", color: C.t2, fontFamily: "monospace", fontSize: 11 }}>RST R</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map(function(q, i) {
                    var key = q.id || (page * PAGE_SIZE + i);
                    var isSel = !!selected[key];
                    return (
                      <tr key={key} onClick={function() { var n = Object.assign({}, selected); n[key] = !n[key]; if (!n[key]) delete n[key]; setSelected(n); }} style={{ borderBottom: "1px solid " + C.bd + "60", cursor: "pointer", background: isSel ? C.a1 + "12" : "transparent", transition: "background .15s" }}>
                        <td style={{ padding: "8px", textAlign: "center" }}>
                          <div style={{ width: 18, height: 18, borderRadius: 4, border: "2px solid " + (isSel ? C.a1 : C.t3), background: isSel ? C.a1 : "transparent", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto" }}>
                            {isSel && <span style={{ color: "#000", fontSize: 12, fontWeight: 700 }}>{"\u2713"}</span>}
                          </div>
                        </td>
                        <td style={{ padding: "8px", color: C.a1, fontWeight: 700, fontFamily: "monospace" }}>{q.call}</td>
                        <td style={{ padding: "8px", color: C.t1, fontFamily: "monospace" }}>{q.date ? q.date.substring(0, 4) + "-" + q.date.substring(4, 6) + "-" + q.date.substring(6, 8) : "—"}</td>
                        <td style={{ padding: "8px", color: C.t2, fontFamily: "monospace" }}>{q.time ? q.time.substring(0, 2) + ":" + q.time.substring(2, 4) : "—"}</td>
                        <td style={{ padding: "8px", color: C.a3, fontFamily: "monospace" }}>{q.band || "—"}</td>
                        <td style={{ padding: "8px", color: C.a4, fontFamily: "monospace" }}>{q.mode || "—"}</td>
                        <td style={{ padding: "8px", color: C.t2, fontFamily: "monospace" }}>{q.rstSent || "—"}</td>
                        <td style={{ padding: "8px", color: C.t2, fontFamily: "monospace" }}>{q.rstRcvd || "—"}</td>
                      </tr>
                    );
                  })}
                  {paged.length === 0 && (
                    <tr><td colSpan={8} style={{ padding: 30, textAlign: "center", color: C.t3 }}>No QSOs found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, padding: "12px 0", borderTop: "1px solid " + C.bd + "40" }}>
                <button disabled={page === 0} onClick={function() { setPage(page - 1); }} style={Object.assign({}, btnStyle, page === 0 ? { opacity: 0.4, cursor: "default" } : {})}>{"<"} Prev</button>
                <span style={{ color: C.t2, fontSize: 12, fontFamily: "monospace" }}>{page + 1} / {totalPages}</span>
                <button disabled={page >= totalPages - 1} onClick={function() { setPage(page + 1); }} style={Object.assign({}, btnStyle, page >= totalPages - 1 ? { opacity: 0.4, cursor: "default" } : {})}>Next {">"}</button>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* TAB: PREVIEW & SEND */}
      {tab === "preview" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Design / template selector */}
          <Card style={{ padding: 14 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
              <span style={{ fontWeight: 700, color: C.t1, fontSize: 13, fontFamily: "monospace" }}>{t(lang, "qslCardStyle")}:</span>
              <select value={selDesignId} onChange={function(e) { setSelDesignId(e.target.value); }} style={selectStyle}>
                <option value="">{t(lang, "qslClassicTemplates")}</option>
                {designsList.map(function(d) {
                  return <option key={d.id} value={String(d.id)}>{(d.is_default ? "\u2605 " : "") + d.name}</option>;
                })}
              </select>
              <button onClick={function() { setTab("design"); }} style={Object.assign({}, btnStyle, { fontSize: 12 })}>{"\u{1F3A8}"} {t(lang, "qslDesignStudioTab")}</button>
            </div>
          </Card>
          {/* Template picker (only for classic templates) */}
          {selDesignId === "" && (
          <Card style={{ padding: 14 }}>
            <div style={{ fontWeight: 700, color: C.t1, fontSize: 13, marginBottom: 10, fontFamily: "monospace", textAlign: "center" }}>{t(lang, "qslChooseTemplate")}</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center", paddingBottom: 8 }}>
              {TEMPLATES.map(function(tp) {
                var active = tplId === tp.id;
                return (
                  <div key={tp.id} onClick={function() { setTplId(tp.id); }} style={{
                    minWidth: 100, padding: "10px 12px", borderRadius: 8, cursor: "pointer", textAlign: "center",
                    background: active ? tp.bg : C.s1,
                    border: "2px solid " + (active ? tp.accent : C.bd),
                    transition: "all .2s"
                  }}>
                    <div style={{ color: active ? tp.text : C.t1, fontSize: 12, fontWeight: 700, fontFamily: "monospace" }}>{tp.name}</div>
                  </div>
                );
              })}
            </div>
          </Card>
          )}

          {/* QSO list for preview */}
          {selectedCount === 0 && (
            <Card style={{ padding: 30, textAlign: "center" }}>
              <div style={{ color: C.t3, fontSize: 14 }}>{t(lang, "qslNoQsoSelected")} <button onClick={function() { setTab("select"); }} style={Object.assign({}, btnStyle, { display: "inline" })}>{t(lang, "qslSelectQsos")}</button> {t(lang, "qslFirst")}</div>
            </Card>
          )}

          {selectedCount > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14, alignItems: "center" }}>
              {/* QSO list */}
              <Card style={{ padding: 14, width: "100%", maxWidth: 700 }}>
                <div style={{ fontWeight: 700, color: C.t1, fontSize: 13, marginBottom: 10, fontFamily: "monospace", textAlign: "center" }}>{selectedCount} QSO{selectedCount > 1 ? "s" : ""} {t(lang, "qslSelected")} — {t(lang, "qslClickPreview")}</div>
                <div style={{ maxHeight: 200, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
                  {qsos.filter(function(q) { return selected[q.id]; }).map(function(q) {
                    var isActive = previewQso && previewQso.id === q.id;
                    return (
                      <div key={q.id} onClick={function() {
                        setPreviewQso(Object.assign({}, q, { _myCall: props.myCallsign }));
                        setSendResult(null);
                        setEmailInput("");
                        setLookupResult(null);
                        lookupEmail(q.call, function(d) { setLookupResult(d); if (d.email) setEmailInput(d.email); });
                      }} style={{
                        display: "flex", justifyContent: "space-between", padding: "8px 10px", borderRadius: 6, cursor: "pointer",
                        background: isActive ? C.a1 + "20" : C.s1,
                        border: "1px solid " + (isActive ? C.a1 + "50" : "transparent"),
                        transition: "all .15s"
                      }}>
                        <span style={{ color: C.a1, fontWeight: 700, fontFamily: "monospace", fontSize: 13 }}>{q.call}</span>
                        <span style={{ color: C.t3, fontFamily: "monospace", fontSize: 11 }}>{q.band} {q.mode}</span>
                      </div>
                    );
                  })}
                </div>
                {/* Message input */}
                <div style={{ marginTop: 12 }}>
                  <div style={{ color: C.t2, fontSize: 11, marginBottom: 4, fontFamily: "monospace" }}>{t(lang, "qslPersonalMsg")}</div>
                  <input value={message} onChange={function(e) { setMessage(e.target.value); }} placeholder="73 de PP5GP!" maxLength={100} style={Object.assign({}, inputStyle, { width: "100%", boxSizing: "border-box" })} />
                </div>
              </Card>

              {/* Batch Queue Send */}
              {selectedCount > 1 && (
                <Card style={{ padding: 14, width: "100%", maxWidth: 700, background: C.a1 + "08", border: "1px solid " + C.a1 + "25" }}>
                  <div style={{ fontWeight: 700, color: C.a1, fontSize: 13, marginBottom: 8, fontFamily: "monospace", textAlign: "center" }}>
                    {t(lang, "qslBatchTitle")}
                  </div>
                  <div style={{ color: C.t2, fontSize: 11, fontFamily: "monospace", textAlign: "center", marginBottom: 10 }}>
                    {t(lang, "qslBatchDesc")} {queueStatus ? t(lang, "qslDailyLimit") + ": " + queueStatus.daily_limit + " | " + t(lang, "qslSentToday") + ": " + queueStatus.sent_today : ""}
                  </div>
                  <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                    <button onClick={queueBatchSend} disabled={queueBusy || queuePreparing} style={Object.assign({}, btnAccent, (queueBusy || queuePreparing) ? { opacity: 0.5, cursor: "wait" } : {})}>
                      {queuePreparing ? t(lang, "qslPreparing") + " " + queuePrepProgress.current + "/" + queuePrepProgress.total + "..." : queueBusy ? t(lang, "qslQueuing") : t(lang, "qslQueueBtn") + " " + selectedCount + " " + t(lang, "qslQueueBtnSuffix")}
                    </button>
                  </div>
                  {selectedCount > 0 && (
                    <div style={{ color: C.t3, fontSize: 10, fontFamily: "monospace", textAlign: "center", marginTop: 6 }}>
                      {t(lang, "qslEstimatedTime")}: ~{Math.ceil(selectedCount * 90 / 60)} min ({selectedCount} x 90s)
                    </div>
                  )}
                  {sendResult && (
                    <div style={{ padding: "8px 12px", borderRadius: 6, fontSize: 12, fontFamily: "monospace", marginTop: 8, background: sendResult.ok ? C.a2 + "15" : C.a5 + "15", color: sendResult.ok ? C.a2 : C.a5, border: "1px solid " + (sendResult.ok ? C.a2 : C.a5) + "30", textAlign: "center" }}>
                      {sendResult.msg}
                    </div>
                  )}
                </Card>
              )}

              {/* Preview + Actions (single send) */}
              <Card style={{ padding: 14, width: "100%", maxWidth: 700 }}>
                  {previewQso ? (
                    <div>
                      <canvas ref={canvasRef} style={{ width: "100%", borderRadius: 8, display: "block" }}></canvas>
                      {/* Email + Send */}
                      <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <input value={emailInput} onChange={function(e) { setEmailInput(e.target.value); }} placeholder={t(lang, "qslRecipientEmail")} style={Object.assign({}, inputStyle, { flex: 1 })} />
                          {lookupResult && lookupResult.source !== "not_found" && lookupResult.source !== "error" && (
                            <span style={{ color: C.a2, fontSize: 10, fontFamily: "monospace", whiteSpace: "nowrap" }}>{lookupResult.source}</span>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button onClick={downloadCard} style={btnStyle}>{"\u{1F4BE}"} {t(lang, "qslDownloadPng")}</button>
                          <button onClick={function() {
                            if (!emailInput || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput)) { setSendResult({ ok: false, msg: t(lang, "qslEmailInvalid") }); return; }
                            sendQSL(previewQso, emailInput);
                          }} disabled={sending} style={Object.assign({}, btnAccent, sending ? { opacity: 0.5, cursor: "wait" } : {})}>
                            {sending ? t(lang, "qslSending") : "\u{1F4E7} " + t(lang, "qslSendEqsl")}
                          </button>
                        </div>
                        {sendResult && (
                          <div style={{ padding: "8px 12px", borderRadius: 6, fontSize: 12, fontFamily: "monospace", background: sendResult.ok ? C.a2 + "15" : C.a5 + "15", color: sendResult.ok ? C.a2 : C.a5, border: "1px solid " + (sendResult.ok ? C.a2 : C.a5) + "30" }}>
                            {sendResult.msg}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div style={{ padding: 40, textAlign: "center", color: C.t3 }}>
                      {"\u2190"} {t(lang, "qslClickPreview")}
                    </div>
                  )}
              </Card>
            </div>
          )}
        </div>
      )}

      {/* TAB: QUEUE */}
      {tab === "queue" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {queueStatus && queueStatus.total > 0 ? (
            <Card style={{ padding: 14 }}>
              {/* Progress bar */}
              <div style={{ fontWeight: 700, color: (queueStatus.pending + queueStatus.sending) > 0 ? C.a2 : C.t1, fontSize: 14, marginBottom: 12, fontFamily: "monospace", textAlign: "center" }}>
                {(queueStatus.pending + queueStatus.sending) > 0 ? t(lang, "qslQueueActive") : t(lang, "qslQueueComplete")}
              </div>
              <div style={{ background: C.s1, borderRadius: 8, height: 24, overflow: "hidden", marginBottom: 10 }}>
                <div style={{
                  height: "100%",
                  background: queueStatus.errors > 0 && queueStatus.sent === 0 ? C.a5 : "linear-gradient(90deg, " + C.a2 + ", " + C.a1 + ")",
                  borderRadius: 8,
                  width: (queueStatus.total > 0 ? Math.round(((queueStatus.sent + queueStatus.errors) / queueStatus.total) * 100) : 0) + "%",
                  transition: "width 0.5s ease"
                }}></div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-around", fontFamily: "monospace", fontSize: 12, marginBottom: 12 }}>
                <div style={{ textAlign: "center" }}><div style={{ color: C.a2, fontSize: 20, fontWeight: 700 }}>{queueStatus.sent}</div><div style={{ color: C.t3, fontSize: 10 }}>{t(lang, "qslSentSection")}</div></div>
                <div style={{ textAlign: "center" }}><div style={{ color: C.a3, fontSize: 20, fontWeight: 700 }}>{queueStatus.pending + queueStatus.sending}</div><div style={{ color: C.t3, fontSize: 10 }}>{t(lang, "qslPendingSection")}</div></div>
                <div style={{ textAlign: "center" }}><div style={{ color: C.a5, fontSize: 20, fontWeight: 700 }}>{queueStatus.errors}</div><div style={{ color: C.t3, fontSize: 10 }}>{t(lang, "qslNoEmailFound")}</div></div>
                <div style={{ textAlign: "center" }}><div style={{ color: C.t1, fontSize: 20, fontWeight: 700 }}>{queueStatus.total}</div><div style={{ color: C.t3, fontSize: 10 }}>Total</div></div>
              </div>
              {/* Countdown */}
              {(queueStatus.pending + queueStatus.sending) > 0 && (
                <div style={{ textAlign: "center", padding: "10px 0", background: C.s1, borderRadius: 8, marginBottom: 12 }}>
                  <div style={{ color: C.t2, fontFamily: "monospace", fontSize: 12 }}>{t(lang, "qslNextEmail")}: <span style={{ color: C.a1, fontWeight: 700, fontSize: 20 }}>{queueCountdown}s</span></div>
                  <div style={{ color: C.t3, fontFamily: "monospace", fontSize: 10, marginTop: 4 }}>{t(lang, "qslRemaining")}: ~{Math.ceil(queueStatus.estimated_remaining_seconds / 60)} min | {t(lang, "qslDaily")}: {queueStatus.sent_today}/{queueStatus.daily_limit}</div>
                </div>
              )}
              {/* Full item lists */}
              {queueStatus.items && queueStatus.items.length > 0 && (
                <div>
                  {/* Sent */}
                  {queueStatus.items.filter(function(it) { return it.status === "sent"; }).length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ color: C.a2, fontSize: 12, fontWeight: 700, fontFamily: "monospace", marginBottom: 6 }}>{"\u2705"} {t(lang, "qslSentSection")} ({queueStatus.items.filter(function(it) { return it.status === "sent"; }).length})</div>
                      <div style={{ borderRadius: 6, border: "1px solid " + C.a2 + "20", overflow: "hidden" }}>
                        {queueStatus.items.filter(function(it) { return it.status === "sent"; }).map(function(item) {
                          return (
                            <div key={item.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 12px", borderBottom: "1px solid " + C.bd + "20", background: C.a2 + "05" }}>
                              <span style={{ color: C.a1, fontFamily: "monospace", fontSize: 12, fontWeight: 600 }}>{item.to_callsign}</span>
                              <span style={{ color: C.t2, fontFamily: "monospace", fontSize: 11 }}>{item.to_email}</span>
                              <span style={{ color: C.a2, fontFamily: "monospace", fontSize: 10 }}>{"\u2713"} SENT</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {/* Pending */}
                  {queueStatus.items.filter(function(it) { return it.status === "pending" || it.status === "sending"; }).length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ color: C.a3, fontSize: 12, fontWeight: 700, fontFamily: "monospace", marginBottom: 6 }}>{"\u23F3"} {t(lang, "qslPendingSection")} ({queueStatus.items.filter(function(it) { return it.status === "pending" || it.status === "sending"; }).length})</div>
                      <div style={{ borderRadius: 6, border: "1px solid " + C.a3 + "20", overflow: "hidden" }}>
                        {queueStatus.items.filter(function(it) { return it.status === "pending" || it.status === "sending"; }).map(function(item) {
                          return (
                            <div key={item.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 12px", borderBottom: "1px solid " + C.bd + "20", background: C.a3 + "05" }}>
                              <span style={{ color: C.a1, fontFamily: "monospace", fontSize: 12, fontWeight: 600 }}>{item.to_callsign}</span>
                              <span style={{ color: C.t3, fontFamily: "monospace", fontSize: 11 }}>{t(lang, "qslAwaitingLookup")}</span>
                              <span style={{ color: item.status === "sending" ? C.a3 : C.t3, fontFamily: "monospace", fontSize: 10, fontWeight: 600 }}>{item.status === "sending" ? "SENDING..." : "PENDING"}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {/* No Email Found */}
                  {queueStatus.items.filter(function(it) { return it.status === "error" && it.error_msg && it.error_msg.indexOf("Email not found") >= 0; }).length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ color: C.a5, fontSize: 12, fontWeight: 700, fontFamily: "monospace", marginBottom: 6 }}>{"\u{1F4ED}"} {t(lang, "qslNoEmailFound")} ({queueStatus.items.filter(function(it) { return it.status === "error" && it.error_msg && it.error_msg.indexOf("Email not found") >= 0; }).length})</div>
                      <div style={{ padding: "8px 12px", color: C.t3, fontSize: 11, fontFamily: "monospace", background: C.a5 + "08", borderRadius: "6px 6px 0 0", borderBottom: "1px solid " + C.a5 + "15" }}>
                        {t(lang, "qslNoEmailDesc")}
                      </div>
                      <div style={{ borderRadius: "0 0 6px 6px", border: "1px solid " + C.a5 + "20", overflow: "hidden" }}>
                        {queueStatus.items.filter(function(it) { return it.status === "error" && it.error_msg && it.error_msg.indexOf("Email not found") >= 0; }).map(function(item) {
                          return (
                            <div key={item.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 12px", borderBottom: "1px solid " + C.bd + "20", background: C.a5 + "05" }}>
                              <span style={{ color: C.a5, fontFamily: "monospace", fontSize: 12, fontWeight: 700 }}>{item.to_callsign}</span>
                              <span style={{ color: C.t3, fontFamily: "monospace", fontSize: 11 }}>{t(lang, "qslNoEmailHamqth")}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* Cancel */}
              {(queueStatus.pending + queueStatus.sending) > 0 && (
                <div style={{ textAlign: "center", marginTop: 10 }}>
                  <button onClick={cancelQueue} style={Object.assign({}, btnWarn, { fontSize: 11 })}>{t(lang, "qslCancelPending")}</button>
                </div>
              )}
            </Card>
          ) : (
            <Card style={{ padding: 30, textAlign: "center" }}>
              <div style={{ color: C.t3, fontSize: 14 }}>{t(lang, "qslNoHistory")}</div>
            </Card>
          )}
        </div>
      )}

      {/* TAB: NO EMAIL — QSOs whose email could not be auto-resolved */}
      {tab === "noemail" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Card style={{ padding: 14 }}>
            <div style={{ fontWeight: 700, color: C.a5, fontSize: 15, marginBottom: 6, fontFamily: "monospace" }}>
              {"\u{1F4ED}"} {t(lang, "qslNoEmailTabTitle")}
            </div>
            <div style={{ color: C.t2, fontSize: 12, fontFamily: "monospace", marginBottom: 12 }}>
              {t(lang, "qslNoEmailTabDesc")}
            </div>
            {noEmailResult && (
              <div style={{ padding: "8px 12px", borderRadius: 6, fontSize: 12, fontFamily: "monospace", marginBottom: 10, background: noEmailResult.ok ? C.a2 + "15" : C.a5 + "15", color: noEmailResult.ok ? C.a2 : C.a5, border: "1px solid " + (noEmailResult.ok ? C.a2 : C.a5) + "30" }}>
                {noEmailResult.msg}
              </div>
            )}
            {(!queueStatus || !queueStatus.no_email_items || queueStatus.no_email_items.length === 0) ? (
              <div style={{ padding: 30, textAlign: "center", color: C.t3, fontFamily: "monospace", fontSize: 13 }}>
                {t(lang, "qslNoEmailEmpty")}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {queueStatus.no_email_items.map(function(item) {
                  var qso = item.qso_id ? qsos.find(function(q) { return q.id === item.qso_id; }) : null;
                  var busy = !!noEmailBusy[item.id];
                  var inputVal = noEmailInputs[item.id] || "";
                  return (
                    <div key={item.id} style={{ background: C.s1, border: "1px solid " + C.a5 + "25", borderRadius: 8, padding: "10px 12px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
                        <div>
                          <span style={{ color: C.a1, fontWeight: 700, fontFamily: "monospace", fontSize: 14 }}>{item.to_callsign}</span>
                          {qso && (
                            <span style={{ color: C.t3, fontFamily: "monospace", fontSize: 11, marginLeft: 10 }}>
                              {qso.band || ""} {qso.mode || ""} {qso.date ? " \u2022 " + qso.date.substring(0,4) + "-" + qso.date.substring(4,6) + "-" + qso.date.substring(6,8) : ""}
                            </span>
                          )}
                        </div>
                        <span style={{ color: C.a5, fontFamily: "monospace", fontSize: 10 }}>{"\u{1F4ED}"} {t(lang, "qslNoEmailHamqth")}</span>
                      </div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                        <input
                          value={inputVal}
                          onChange={function(e) {
                            var v = e.target.value;
                            setNoEmailInputs(function(prev) { var n = Object.assign({}, prev); n[item.id] = v; return n; });
                          }}
                          placeholder={t(lang, "qslEnterEmailPlaceholder")}
                          disabled={busy}
                          style={Object.assign({}, inputStyle, { flex: 1, minWidth: 220 })}
                        />
                        <button
                          onClick={function() { retryWithEmail(item.id, inputVal); }}
                          disabled={busy || !inputVal}
                          style={Object.assign({}, btnAccent, (busy || !inputVal) ? { opacity: 0.5, cursor: "default" } : {})}
                        >
                          {"\u{1F4E7}"} {t(lang, "qslRetryWithEmail")}
                        </button>
                        <button
                          onClick={function() { dismissNoEmail(item.id); }}
                          disabled={busy}
                          style={Object.assign({}, btnWarn, busy ? { opacity: 0.5, cursor: "default" } : {})}
                        >
                          {t(lang, "qslDismiss")}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* TAB: HISTORY */}
      {tab === "history" && (
        <Card style={{ padding: 14 }}>
          {!history ? (
            <div style={{ textAlign: "center", padding: 30, color: C.t3 }}>{t(lang, "loading")}</div>
          ) : history.total === 0 ? (
            <div style={{ textAlign: "center", padding: 30, color: C.t3 }}>{t(lang, "qslNoHistory")}</div>
          ) : (
            <div>
              {/* Stats */}
              <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
                {(history.stats || []).map(function(s) {
                  var color = s.status === "sent" ? C.a2 : s.status === "error" ? C.a5 : C.a6;
                  var label = s.status === "sent" ? t(lang, "qslStatusSent") : s.status === "error" ? t(lang, "qslStatusError") : t(lang, "qslStatusPending");
                  return (
                    <div key={s.status} style={{ background: color + "12", border: "1px solid " + color + "30", borderRadius: 8, padding: "8px 14px" }}>
                      <span style={{ color: color, fontWeight: 700, fontFamily: "monospace", fontSize: 13 }}>{s.count}</span>
                      <span style={{ color: C.t2, fontSize: 11, marginLeft: 6 }}>{label}</span>
                    </div>
                  );
                })}
              </div>
              {/* Table */}
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: C.s3 }}>
                      <th style={{ padding: "10px 8px", textAlign: "left", color: C.t2, fontFamily: "monospace", fontSize: 11 }}>{t(lang, "qslHistoryTo")}</th>
                      <th style={{ padding: "10px 8px", textAlign: "left", color: C.t2, fontFamily: "monospace", fontSize: 11 }}>{t(lang, "qslHistoryEmail")}</th>
                      <th style={{ padding: "10px 8px", textAlign: "left", color: C.t2, fontFamily: "monospace", fontSize: 11 }}>{t(lang, "qslHistoryTemplate")}</th>
                      <th style={{ padding: "10px 8px", textAlign: "left", color: C.t2, fontFamily: "monospace", fontSize: 11 }}>{t(lang, "qslHistoryStatus")}</th>
                      <th style={{ padding: "10px 8px", textAlign: "left", color: C.t2, fontFamily: "monospace", fontSize: 11 }}>{t(lang, "qslHistorySentAt")}</th>
                      <th style={{ padding: "10px 8px", textAlign: "center", color: C.t2, fontFamily: "monospace", fontSize: 11 }}>QSL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.records.map(function(r) {
                      var statusColor = r.status === "sent" ? C.a2 : r.status === "error" ? C.a5 : C.a6;
                      var statusLabel = r.status === "sent" ? t(lang, "qslStatusSent") : r.status === "error" ? t(lang, "qslStatusError") : t(lang, "qslStatusPending");
                      return (
                        <tr key={r.id} style={{ borderBottom: "1px solid " + C.bd + "40" }}>
                          <td style={{ padding: "8px", color: C.a1, fontWeight: 700, fontFamily: "monospace" }}>{r.to_callsign}</td>
                          <td style={{ padding: "8px", color: C.t2, fontFamily: "monospace", fontSize: 11 }}>{r.to_email}</td>
                          <td style={{ padding: "8px", color: C.t2, fontFamily: "monospace" }}>{r.template_id}</td>
                          <td style={{ padding: "8px" }}>
                            <span style={{ background: statusColor + "18", color: statusColor, padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{statusLabel}</span>
                          </td>
                          <td style={{ padding: "8px", color: C.t3, fontFamily: "monospace", fontSize: 11 }}>{r.sent_at || "\u2014"}</td>
                          <td style={{ padding: "8px", textAlign: "center" }}>
                            {r.card_path ? (
                              <button onClick={function() { setCardModal({ callsign: r.to_callsign, path: r.card_path, sentAt: r.sent_at }); }} style={{ background: C.a1 + "18", color: C.a1, border: "1px solid " + C.a1 + "40", borderRadius: 4, padding: "2px 8px", fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: "monospace" }}>
                                {t(lang, "qslViewCard")}
                              </button>
                            ) : (
                              <span style={{ color: C.t3, fontSize: 10 }}>{"\u2014"}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Card>
      )}
      {/* QSL Card Image Modal */}
      {cardModal && (
        <div onClick={function() { setCardModal(null); }} style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.85)", zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer"
        }}>
          <div onClick={function(e) { e.stopPropagation(); }} style={{
            background: C.s1, borderRadius: 12, padding: 16, maxWidth: 700, width: "90%",
            border: "1px solid " + C.a1 + "40", cursor: "default"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div>
                <span style={{ color: C.a1, fontWeight: 700, fontFamily: "monospace", fontSize: 14 }}>QSL → {cardModal.callsign}</span>
                {cardModal.sentAt && <span style={{ color: C.t3, fontSize: 11, marginLeft: 10, fontFamily: "monospace" }}>{cardModal.sentAt}</span>}
              </div>
              <button onClick={function() { setCardModal(null); }} style={{ background: "none", border: "none", color: C.t2, fontSize: 20, cursor: "pointer", padding: "0 4px" }}>{"\u2715"}</button>
            </div>
            <img src={cardModal.path} alt={"QSL " + cardModal.callsign} style={{ width: "100%", borderRadius: 8, display: "block" }} />
            <div style={{ marginTop: 10, textAlign: "center" }}>
              <a href={cardModal.path} download={"qsl_" + cardModal.callsign + ".jpg"} style={{ color: C.a1, fontSize: 11, fontFamily: "monospace", textDecoration: "none" }}>
                {t(lang, "qslDownloadPng")}
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
